import React, { useEffect, useMemo, useState } from 'react';
import './Gallery.css';

// ----------------------------------------------------------------------
// CONFIGURATION
// ----------------------------------------------------------------------
// To avoid API keys and costs, we use a free Google Apps Script.
// 1. Open src/pages/GalleryBackendScript.txt
// 2. Follow the instructions to create and deploy the script.
// 3. Paste the 'Web app URL' below.
const GOOGLE_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbwsyXMxjGXzrQBAVFND0AusffhoWMoLov8sdzaMP4dvVYdtQqklAcI00k4xMsaeZ8-2/exec'; // <--- PASTE YOUR WEB APP URL HERE
// ----------------------------------------------------------------------

const CACHE_KEY = 'gallery_manifest_v1';
const CACHE_DURATION = 60 * 60 * 1000; // 1 hour

const Gallery = () => {
  const [manifest, setManifest] = useState({});
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedEvent, setSelectedEvent] = useState('All');
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    async function loadGallery() {
      if (!GOOGLE_SCRIPT_URL) {
        setError('Missing Backend URL');
        setIsLoaded(true);
        return;
      }

      // 1. Try to load from local cache first
      try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          const { timestamp, data } = JSON.parse(cached);
          if (Date.now() - timestamp < CACHE_DURATION && data && Object.keys(data).length > 0) {
            console.log('Loaded gallery from cache');
            setManifest(data);
            setIsLoaded(true);
            return; // Exit if cache is valid
          }
        }
      } catch (e) {
        console.warn('Failed to parse gallery cache', e);
      }

      // 2. Fetch from network if no cache or expired
      try {
        const res = await fetch(GOOGLE_SCRIPT_URL);
        if (!res.ok) throw new Error('Failed to fetch from script');
        
        const data = await res.json();
        
        if (data.error) {
          throw new Error(data.error);
        }

        setManifest(data);
        setIsLoaded(true);

        // Save to cache
        localStorage.setItem(CACHE_KEY, JSON.stringify({
          timestamp: Date.now(),
          data: data
        }));

      } catch (err) {
        console.error(err);
        setError(`Failed to load gallery (${err.message || 'Error'}). Ensure the script is deployed as "Anyone" and the URL is correct.`);
        setIsLoaded(true);
      }
    }

    loadGallery();
  }, []);

  // Parse categories into Year and clean Event Name
  const { yearList, eventsByYear, allImages } = useMemo(() => {
    const yearsSet = new Set();
    const byYear = {};
    const imagesList = [];

    Object.entries(manifest).forEach(([rawKey, files]) => {
      let year = 'Other';
      let eventName = rawKey;

      if (rawKey.includes(' - ')) {
        const firstDash = rawKey.indexOf(' - ');
        const prefix = rawKey.substring(0, firstDash).trim();
        eventName = rawKey.substring(firstDash + 3).trim();

        // Extract year pattern like 2025-26 or 2026-27
        const match = prefix.match(/\d{4}[-–]\d{2,4}/);
        if (match) {
          year = match[0];
        } else {
          year = prefix;
        }
      }

      yearsSet.add(year);
      if (!byYear[year]) {
        byYear[year] = {};
      }
      byYear[year][eventName] = files || [];

      (files || []).forEach((file) => {
        const originalSrc = file.src || '';
        const thumbnailSrc = originalSrc.replace(/sz=w\d+/, 'sz=s400');
        const fullSrc = originalSrc.replace(/sz=w\d+/, 'sz=s2000');

        imagesList.push({
          id: file.id,
          src: thumbnailSrc,
          fullSrc: fullSrc,
          alt: file.name,
          year: year,
          eventName: eventName
        });
      });
    });

    // Sort years descending (newest year first)
    const sortedYears = Array.from(yearsSet).sort().reverse();

    return {
      yearList: sortedYears,
      eventsByYear: byYear,
      allImages: imagesList
    };
  }, [manifest]);

  // Set default selected year to first year
  useEffect(() => {
    if (yearList.length > 0 && (!selectedYear || !yearList.includes(selectedYear))) {
      setSelectedYear(yearList[0]);
    }
  }, [yearList, selectedYear]);

  // Available events for currently selected year
  const availableEvents = useMemo(() => {
    if (!selectedYear) return [];
    return Object.keys(eventsByYear[selectedYear] || {}).sort();
  }, [eventsByYear, selectedYear]);

  // Filter images based on selected year and event
  const displayedImages = useMemo(() => {
    return allImages.filter((img) => {
      const matchesYear = img.year === selectedYear;
      const matchesEvent = selectedEvent === 'All' || img.eventName === selectedEvent;
      return matchesYear && matchesEvent;
    });
  }, [allImages, selectedYear, selectedEvent]);

  return (
    <main className="gallery-page">
      <div className="gallery-container">
        <div className="gallery-header">
          <h1>Gallery</h1>

          {/* Academic Year Tabs */}
          {yearList.length > 1 && (
            <div className="gallery-year-tabs">
              {yearList.map((yr) => (
                <button
                  key={yr}
                  className={`year-tab ${selectedYear === yr ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedYear(yr);
                    setSelectedEvent('All');
                  }}
                >
                  {yr}
                </button>
              ))}
            </div>
          )}

          {/* Clean Event Filter Buttons */}
          {availableEvents.length > 0 && (
            <div className="gallery-menu">
              <button
                className={`menu-btn ${selectedEvent === 'All' ? 'active' : ''}`}
                onClick={() => setSelectedEvent('All')}
              >
                All Events
              </button>
              {availableEvents.map((ev) => (
                <button
                  key={ev}
                  className={`menu-btn ${selectedEvent === ev ? 'active' : ''}`}
                  onClick={() => setSelectedEvent(ev)}
                >
                  {ev}
                </button>
              ))}
            </div>
          )}
        </div>

        {!isLoaded ? (
          <div className="gallery-loading">Loading gallery...</div>
        ) : error ? (
          <div className="gallery-error">
            <p>{error}</p>
            {!GOOGLE_SCRIPT_URL && (
              <div style={{textAlign: 'left', marginTop: '15px', background: '#f8d7da', padding: '15px', borderRadius: '5px', color: '#721c24'}}>
                <strong>Action Required:</strong>
                <ol style={{marginLeft: '20px', marginTop: '10px'}}>
                  <li>Open the file <code>src/pages/GalleryBackendScript.txt</code> in this project.</li>
                  <li>Copy the code inside.</li>
                  <li>Go to <a href="https://script.google.com/home/start" target="_blank" rel="noreferrer">script.google.com</a> and create a new project.</li>
                  <li>Paste the code and click <strong>Deploy &gt; New deployment</strong>.</li>
                  <li>Set <em>Who has access</em> to <strong>Anyone</strong>.</li>
                  <li>Copy the resulting Web App URL.</li>
                  <li>Paste it into <code>src/pages/Gallery.js</code> (at the top).</li>
                </ol>
              </div>
            )}
          </div>
        ) : displayedImages.length === 0 ? (
          <div className="gallery-empty" style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
            <p>No photos available for this event yet.</p>
          </div>
        ) : (
          <div className="gallery-grid">
            {displayedImages.map((img, i) => (
              <a
                key={`${img.src}-${i}`}
                href={img.fullSrc}
                target="_blank"
                rel="noopener noreferrer"
                className="gallery-item"
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  referrerPolicy="no-referrer"
                  onError={(e) => {
                    // If thumbnail fails, try loading the full resolution image
                    if (e.currentTarget.src !== img.fullSrc) {
                      e.currentTarget.src = img.fullSrc;
                    }
                  }}
                />
              </a>
            ))}
          </div>
        )}
      </div>
    </main>
  );
};

export default Gallery;

